import { getSession } from '../auth';
import { listPayrollForWeek, listSettlements, listPayoutRequests, isProductionData } from '../lib/repository';
import { calculateSettlement, calculatePayout } from '../commerce';

const money = (n: number) => '₹' + Math.round(n).toLocaleString('en-IN');

let payrollRows: Awaited<ReturnType<typeof listPayrollForWeek>> | null = null;
let settlements: Awaited<ReturnType<typeof listSettlements>> | null = null;
let payoutReqs: Awaited<ReturnType<typeof listPayoutRequests>> | null = null;
let treasuryLoaded = false;

async function loadTreasury() {
  if (treasuryLoaded || !isProductionData()) return;
  try {
    const [p, s, r] = await Promise.all([
      listPayrollForWeek(),
      listSettlements(),
      listPayoutRequests().catch(() => []),
    ]);
    payrollRows = p; settlements = s; payoutReqs = r; treasuryLoaded = true;
  } catch {
    treasuryRows = []; settlements = []; payoutReqs = [];
  }
  (window as any).__rerender?.();
}

let treasuryRows: Awaited<ReturnType<typeof listPayrollForWeek>> = [];

export function renderTreasury() {
  void loadTreasury();
  const session = getSession();
  const isOwner = !!session && (session.role === 'owner' || session.role === 'admin');
  const payroll = payrollRows ?? [];
  const setls = settlements ?? [];
  const weekRevenue = setls.reduce((s, x) => s + (x.settlementAmount || 0), 0);
  const weekOutflow = payroll.reduce((s, p) => s + (p.net || 0), 0);
  const pendingApprovals = (payoutReqs || []).filter((r) => r.status === 'requested').length;
  const inflowPct = weekRevenue > 0 ? Math.min(100, (weekRevenue / Math.max(weekRevenue + weekOutflow, 1)) * 100) : 0;
  const outflowPct = weekOutflow > 0 ? Math.min(100 - inflowPct, (weekOutflow / Math.max(weekRevenue + weekOutflow, 1)) * 100) : 0;
  const haveData = payroll.length > 0 || setls.length > 0;
  const payoutCards = payroll.slice(0, 8).map((p) => `
    <div class="row"><div class="avatar av-c2">${(p.employeeName || '?').slice(0,2).toUpperCase()}</div>
      <div style="flex:1"><div class="t-strong">${p.employeeName || p.employeeUid}</div>
      <div class="t-meta">${p.hours || 0}h · ${p.role || 'staff'}</div></div>
      <div class="t-mono t-strong">${money(p.net || 0)}</div></div>`).join('');

  if (!haveData && !treasuryLoaded) {
    return `<section class="hero stagger"><div><div class="hero-greet">Treasury</div><h1 class="hero-title">Money in,<br/><span class="hero-stroke">money out.</span></h1></div></section>
    <div class="treasury-grid stagger">
      <div class="card card-pad-lg"><div class="t-eyebrow">Week revenue</div><div class="treasury-stat"><div class="ts-val">${money(0)}</div></div><div class="t-meta">Loading…</div></div>
      <div class="card card-pad-lg"><div class="t-eyebrow">Pending payouts</div><div class="treasury-stat"><div class="ts-val">${money(0)}</div></div><div class="t-meta">— approvals</div></div>
    </div>
    <div class="card card-pad-lg stagger" style="margin-top:16px"><div class="t-eyebrow">This week's payouts</div><div class="empty" style="padding:24px 0">No payroll records for this week yet.</div></div>`;
  }

  if (!haveData) {
    return `<section class="hero stagger"><div><div class="hero-greet">Treasury</div><h1 class="hero-title">Money in,<br/><span class="hero-stroke">money out.</span></h1></div>
      <div class="hero-aside">${isOwner ? '<button class="btn btn-accent touch-target" data-action="payout-new">+ Payout batch</button>' : ''}</div></section>
    <div class="treasury-grid stagger">
      <div class="card card-pad-lg"><div class="t-eyebrow">Week revenue</div><div class="treasury-stat"><div class="ts-val">${money(0)}</div></div><div class="t-meta">From commerce settlements</div></div>
      <div class="card card-pad-lg"><div class="t-eyebrow">Pending payouts</div><div class="treasury-stat"><div class="ts-val">${money(0)}</div></div><div class="t-meta">${pendingApprovals} approval(s) queued</div></div>
    </div>
    <div class="card card-pad-lg stagger" style="margin-top:16px"><div class="t-eyebrow">This week's payouts</div><div class="empty" style="padding:32px 0;text-align:center"><h3 style="margin:0 0 6px">No payroll records yet</h3><p class="t-meta">Records will appear here once your finance team marks payslips as ready.</p></div></div>`;
  }

  return `<section class="hero stagger"><div><div class="hero-greet">Treasury · current week</div><h1 class="hero-title">Money in,<br/><span class="hero-stroke">money out.</span></h1></div>
    <div class="hero-aside"><button class="btn btn-ghost touch-target" data-action="treasury-export">Export</button>${isOwner ? '<button class="btn btn-accent touch-target" data-action="payout-new">+ Payout batch</button>' : ''}</div></section>
  <div class="treasury-grid stagger">
    <div class="card card-pad-lg">
      <div class="row"><div><div class="t-eyebrow">Week revenue</div><div class="treasury-stat" style="margin-top:8px"><div class="ts-val">${money(weekRevenue)}</div></div></div>
      <div class="spacer"></div>
      <div><div class="t-eyebrow">Pending payouts</div><div class="treasury-stat" style="margin-top:8px"><div class="ts-val">${money(weekOutflow)}</div><div class="kpi-delta down">▼ ${pendingApprovals} approval(s)</div></div></div></div>
      <div class="divider-rule" style="margin:var(--s-5) 0">Cashflow this week</div>
      <div class="treasury-bar"><span class="in" style="width:${inflowPct}%"></span><span class="out" style="width:${outflowPct}%"></span></div>
      <div class="row" style="margin-top:8px;font-size:var(--t-xs);color:var(--ink-3);flex-wrap:wrap">
        <span class="row-tight"><span style="display:inline-block;width:10px;height:10px;background:var(--success);border-radius:2px"></span> Inflow · ${money(weekRevenue)}</span>
        <span class="row-tight"><span style="display:inline-block;width:10px;height:10px;background:var(--accent);border-radius:2px"></span> Outflow · ${money(weekOutflow)}</span></div>
    </div>
    <div class="card card-pad-lg"><div class="t-eyebrow" style="margin-bottom:var(--s-3)">This week's payouts</div>
      <div class="stack-tight">${payoutCards || '<div class="empty">No payroll rows this week.</div>'}</div></div>
  </div>`;
}

export function bindTreasury(): void { /* actions delegated from main.ts */ }
